<template>
  <!-- KodeAI: the help assistant's panel, in the support chat's look (the
       popup at the bottom right, components/chat/Support_Chat_Widget): its
       palette, its type, its cards and bubbles. A new conversation opens as
       the chat's Home does — the hello on a pale tint, with the questions to
       start with on a card over its foot; a conversation has
       the chat's white header, each message under a line with who wrote it
       and when, and the chat's plain composer. It is opened from the top bar
       — "Ask KodeAI" on a computer (components/Navbar), the "k" in a page's
       phone top bar (Kode_AI_Button) — and hangs below it, clear of the
       support chat's button at the bottom right. Ask how to do anything in
       Chabad Digital, typed or spoken into the microphone, and it answers from
       what it knows about the app (the API's utils/kode_ai): the steps, the
       exact buttons, and links to the pages. It only explains; it never
       changes anything. Answers can be read aloud (every one, with the
       speaker at the top; one, with the speaker under it; and a spoken
       question is always answered aloud). "×" closes it and keeps the
       conversation; the arrows start a new one. It fills the screen on a
       phone. The conversation is kept for the tab's session, nowhere else. -->
  <div class="kai" :class="{ 'kai--open': open }">
    <transition name="kai-panel">
      <section
        v-if="hasOpened"
        v-show="open"
        ref="panel"
        class="kai-panel"
        role="dialog"
        tabindex="-1"
        aria-label="KodeAI, the Chabad Digital help assistant"
        @keydown.esc="onEscape"
      >
        <!-- In a conversation, white as the support chat's: the mark, the
             name, and the buttons. A new conversation has its own, on the tint. -->
        <header v-if="messages.length" class="kai-head">
          <span class="kai-head__mark" aria-hidden="true"><kode-ai-logo :twinkle="thinking" /></span>
          <h2 class="kai-head__title">KodeAI</h2>
          <button
            v-if="speechSupported"
            type="button"
            class="kai-icon-btn"
            :class="{ 'is-on': voiceReplies }"
            :title="voiceReplies ? 'Voice replies are on — click to turn them off' : 'Read every answer aloud'"
            :aria-label="voiceReplies ? 'Turn voice replies off' : 'Read every answer aloud'"
            :aria-pressed="voiceReplies ? 'true' : 'false'"
            @click="toggleVoiceReplies"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5z" />
              <path d="M15.5 9.25a3.5 3.5 0 0 1 0 5.5" />
              <path v-if="voiceReplies" d="M18.25 6.75a7 7 0 0 1 0 10.5" />
            </svg>
          </button>
          <button
            type="button"
            class="kai-icon-btn"
            title="New conversation"
            aria-label="Start a new conversation"
            :disabled="thinking"
            @click="newConversation"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4.5 12a7.5 7.5 0 0 1 13-5.15L19.5 8.75" />
              <path d="M19.5 4.5v4.25h-4.25" />
              <path d="M19.5 12a7.5 7.5 0 0 1-13 5.15L4.5 15.25" />
              <path d="M4.5 19.5v-4.25h4.25" />
            </svg>
          </button>
          <button type="button" class="kai-icon-btn" title="Close" aria-label="Close KodeAI" @click="close()">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.75 6.75l10.5 10.5M17.25 6.75L6.75 17.25" /></svg>
          </button>
        </header>
        <!-- A new conversation's, on the hello's tint: the mark on its white
             tile, the name and the buttons. It stays put while the hello and
             the cards scroll under it, and a hairline shows once they have. -->
        <header v-else class="kai-home-head" :class="{ 'is-scrolled': scrolled }">
          <span class="kai-home-head__logo" aria-hidden="true"><kode-ai-logo /></span>
          <span class="kai-home-head__name">KodeAI</span>
          <button
            v-if="speechSupported"
            type="button"
            class="kai-icon-btn kai-icon-btn--hero"
            :class="{ 'is-on': voiceReplies }"
            :title="voiceReplies ? 'Voice replies are on — click to turn them off' : 'Read every answer aloud'"
            :aria-label="voiceReplies ? 'Turn voice replies off' : 'Read every answer aloud'"
            :aria-pressed="voiceReplies ? 'true' : 'false'"
            @click="toggleVoiceReplies"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5z" />
              <path d="M15.5 9.25a3.5 3.5 0 0 1 0 5.5" />
              <path v-if="voiceReplies" d="M18.25 6.75a7 7 0 0 1 0 10.5" />
            </svg>
          </button>
          <button type="button" class="kai-icon-btn kai-icon-btn--hero" title="Close" aria-label="Close KodeAI" @click="close()">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.75 6.75l10.5 10.5M17.25 6.75L6.75 17.25" /></svg>
          </button>
        </header>

        <div ref="scroll" class="kai-scroll" @click="onContentClick" @scroll.passive="onScroll">
          <!-- A new conversation, as the support chat's Home: a hello on the
               pale tint (under the header above), then a card over its foot
               with questions to start with. -->
          <section v-if="!messages.length" class="kai-home" aria-label="Getting started with KodeAI">
            <div class="kai-hero">
              <h2 class="kai-hero__title">{{ greeting }} <span class="kai-hero__wave" aria-hidden="true">👋</span></h2>
              <p class="kai-hero__text">How can I help?</p>
            </div>

            <div class="kai-cards">
              <div class="kai-card kai-ask" role="group" aria-label="Suggested questions">
                <span class="kai-card__label" aria-hidden="true">Suggested questions</span>
                <button
                  v-for="question in suggestions"
                  :key="question"
                  type="button"
                  class="kai-ask__row"
                  :disabled="thinking"
                  @click="ask(question)"
                >
                  <span class="kai-ask__text">{{ question }}</span>
                  <i class="pi pi-angle-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </section>

          <!-- The conversation, as the support chat draws one: few messages
               sit at the bottom, by the composer; a run from one side sits
               under one line with who wrote it and when. -->
          <div v-else class="kai-thread">
            <div
              v-for="(message, index) in messages"
              :key="message.id"
              class="kai-msg"
              :class="[`kai-msg--${message.role}`, { 'kai-msg--first': startsRun(index), 'is-failed': message.status === 'failed', 'is-sending': message.status === 'sending' }]"
            >
              <div class="kai-msg__main">
                <div v-if="startsRun(index)" class="kai-msg__head">
                  <span v-if="message.role === 'assistant'" class="kai-avatar" aria-hidden="true"><kode-ai-logo /></span>
                  <span class="kai-msg__name">{{ message.role === 'assistant' ? 'KodeAI' : 'You' }}</span>
                  <span class="kai-msg__time" :title="chatFullTime(message.createdAt)">{{ chatClock(message.createdAt) }}</span>
                </div>
                <div v-if="message.role === 'assistant'" class="kai-bubble kai-md" v-html="renderKodeMarkdown(message.text)"></div>
                <div v-else class="kai-bubble kai-bubble--mine">
                  <i v-if="message.byVoice" class="pi pi-microphone kai-bubble__voice" title="Asked by voice" aria-label="Asked by voice"></i>
                  <span class="kai-bubble__text">{{ message.text }}</span>
                </div>

                <div v-if="message.role === 'assistant'" class="kai-msg__tools">
                  <button
                    v-if="speechSupported"
                    type="button"
                    class="kai-tool"
                    :class="{ 'is-on': speakingId === message.id }"
                    :title="speakingId === message.id ? 'Stop reading' : 'Read aloud'"
                    :aria-label="speakingId === message.id ? 'Stop reading this answer' : 'Read this answer aloud'"
                    @click="toggleSpeak(message)"
                  >
                    <i class="pi" :class="speakingId === message.id ? 'pi-stop-circle' : 'pi-volume-up'" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="kai-tool"
                    :class="{ 'is-on': copiedId === message.id }"
                    :title="copiedId === message.id ? 'Copied' : 'Copy answer'"
                    aria-label="Copy this answer"
                    @click="copyAnswer(message)"
                  >
                    <i class="pi" :class="copiedId === message.id ? 'pi-check' : 'pi-copy'" aria-hidden="true"></i>
                  </button>
                </div>
                <div v-else-if="message.status === 'failed'" class="kai-msg__status" role="status">
                  <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                  <span>{{ message.error || 'Not sent.' }}</span>
                  <button type="button" class="kai-msg__status-btn" :disabled="thinking" @click="retry(message)">Try again</button>
                  <span aria-hidden="true">·</span>
                  <button type="button" class="kai-msg__status-btn" @click="discard(message)">Remove</button>
                </div>
              </div>
            </div>

            <!-- Thinking, as the support chat shows someone typing (and the
                 sparkles of the mark at the top twinkle). -->
            <div v-if="thinking" class="kai-typing" aria-live="polite">
              <span class="kai-typing__dots" aria-hidden="true"><i></i><i></i><i></i></span>
              <span>{{ thinkingLabel }}</span>
            </div>
          </div>
        </div>

        <!-- The support chat's composer: a plain line, the microphone on its
             right while nothing is typed and the send arrow once something
             is (the microphone stays while it listens, to be tapped to stop). -->
        <footer class="kai-foot">
          <div v-if="listening" class="kai-listening" role="status">
            <span class="kai-listening__dot" aria-hidden="true"></span>
            <span class="kai-listening__text">Listening… pause when you're done to send, or tap the microphone to stop and edit.</span>
          </div>
          <!-- Where dictation is not available, the recording is sent for the
               assistant to transcribe (the support chat's own recorder). -->
          <chat-voice-recorder v-if="recording" @send="onRecorded" @cancel="recording = false" />
          <form v-else class="kai-composer" @submit.prevent="submit">
            <textarea
              ref="input"
              v-model="draft"
              class="kai-composer__input"
              rows="1"
              :placeholder="listening ? 'Say your question…' : 'Ask how to do something in Chabad Digital…'"
              :maxlength="maxLength"
              aria-label="Your question"
              enterkeyhint="send"
              @keydown="onKeydown"
              @input="autogrow"
            ></textarea>
            <button
              v-if="showMic"
              type="button"
              class="kai-mic"
              :class="{ 'is-on': listening }"
              :title="micTitle"
              :aria-label="micTitle"
              :aria-pressed="listening ? 'true' : 'false'"
              :disabled="thinking"
              @click="toggleVoice"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="8.5" y="2.25" width="7" height="12.75" rx="3.5" />
                <path d="M5 11.5a7 7 0 0 0 14 0M12 18.5v3.25" />
              </svg>
            </button>
            <button v-else type="submit" class="kai-send" :disabled="!canSend" title="Send" aria-label="Send your question">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21.05 3.05c-2.18-2.35-18.56 3.4-18.55 5.5.02 2.38 6.4 3.11 8.17 3.61 1.06.3 1.35.6 1.6 1.72 1.1 5.05 1.66 7.57 2.93 7.62 2.03.09 7.97-16.16 5.85-18.45Z" />
                <path d="M11.5 12.5L15 9" />
              </svg>
            </button>
          </form>
        </footer>
      </section>
    </transition>
  </div>
</template>
<script>
import chatVoiceRecorder from '@/components/chat/Chat_Voice_Recorder.vue';
import kodeAiLogo from '@/components/kode_ai/Kode_AI_Logo.vue';
import { askKodeAi } from '@/services/kode_ai';
import {
  closeKodeAi,
  createKodeDictation,
  formatSeconds,
  isDictationSupported,
  isRecordingSupported,
  isSpeechSupported,
  KODE_AI_MESSAGE_MAX_LENGTH,
  KODE_AI_OPEN_KEY,
  KODE_AI_SUGGESTIONS,
  KODE_AI_VOICE_REPLIES_KEY,
  kodeAiHistoryOf,
  kodeAiMessageId,
  kodeAiPageOf,
  kodeAiPanel,
  kodeAiPlainText,
  readStorage,
  readStoredMessages,
  recordingToBase64,
  renderKodeMarkdown,
  speakKodeAnswer,
  stopSpeaking,
  writeStorage,
  writeStoredMessages,
} from '@/utils/kode_ai';
import { chatClock, chatFullTime } from '@/utils/chat';
import { NOT_FOUND_404_ROUTE } from '@/router/routes';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';

// The composer grows to this, then scrolls — the support chat's own most (assets/style/chat.css).
const INPUT_MAX_HEIGHT_PX = 160;
// After this long without an answer the panel says it is still working.
const SLOW_AFTER_MS = 8000;
// Below this width the open panel fills the screen (the stylesheet's phone breakpoint).
const PHONE_MAX_WIDTH = 576;

const isTouch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

export default {
  name: 'KodeAiWidget',
  components: {
    chatVoiceRecorder,
    kodeAiLogo,
  },
  data() {
    // Open again if it was open when this tab last showed it.
    if (readStorage('sessionStorage', KODE_AI_OPEN_KEY) === '1') kodeAiPanel.open = true;
    return {
      // The panel is only made once it is first opened.
      hasOpened: kodeAiPanel.open,
      messages: readStoredMessages(),
      draft: '',
      // A question is with the assistant.
      thinking: false,
      thinkingSlow: false,
      // Dictation is on: the words land in the box as they are spoken.
      listening: false,
      // The recorder has the composer's place.
      recording: false,
      // The answer being read aloud, if any; the answer just copied.
      speakingId: null,
      copiedId: null,
      voiceReplies: readStorage('localStorage', KODE_AI_VOICE_REPLIES_KEY) === '1',
      speechSupported: isSpeechSupported(),
      // 'dictation' (Chrome, Edge, Safari: the words as they are spoken),
      // 'recording' (elsewhere: the assistant transcribes a recording), or
      // '' when the browser has no microphone API at all.
      voiceMode: isDictationSupported() ? 'dictation' : isRecordingSupported() ? 'recording' : '',
      // Something has scrolled under a new conversation's header: its hairline shows.
      scrolled: false,
      suggestions: KODE_AI_SUGGESTIONS,
      maxLength: KODE_AI_MESSAGE_MAX_LENGTH,
      isTouch: isTouch(),
    };
  },
  computed: {
    // Opened and closed from the top bars (components/kode_ai/Kode_AI_Button).
    open() {
      return kodeAiPanel.open;
    },
    account() {
      return this.$store.getters.account?.data || null;
    },
    firstName() {
      const name = String(this.account?.fname || '').trim();
      return name ? `${name.charAt(0).toUpperCase()}${name.slice(1)}` : '';
    },
    // "Hi Aman!", as the support chat's Home says it, over "How can I help?".
    greeting() {
      return this.firstName ? `Hi ${this.firstName}!` : 'Hi there!';
    },
    canSend() {
      return !this.thinking && this.draft.trim().length > 0;
    },
    // The microphone while nothing is typed (and while it listens); the send arrow once something is.
    showMic() {
      return Boolean(this.voiceMode) && (this.listening || !this.draft.trim());
    },
    micTitle() {
      if (this.listening) return 'Stop listening';
      return this.voiceMode === 'dictation' ? 'Ask by voice' : 'Record a question';
    },
    thinkingLabel() {
      return this.thinkingSlow ? 'KodeAI is still working on it…' : 'KodeAI is thinking…';
    },
  },
  watch: {
    // Opened: the panel is made if need be, and the cursor goes in.
    open(isOpen) {
      writeStorage('sessionStorage', KODE_AI_OPEN_KEY, isOpen ? '1' : null);
      if (isOpen) {
        this.hasOpened = true;
        this.$nextTick(() => {
          this.scrollToBottom(false);
          this.focusInput();
        });
        return;
      }
      this.stopVoice();
      this.stopReading();
    },
    messages: {
      deep: true,
      handler(list) {
        writeStoredMessages(list);
      },
    },
  },
  methods: {
    renderKodeMarkdown,
    chatClock,
    chatFullTime,
    // A message starts a run when the one before it is the other side's:
    // the run sits under one line, as in the support chat.
    startsRun(index) {
      return index === 0 || this.messages[index - 1].role !== this.messages[index].role;
    },

    /* ------------------------------ closing ------------------------------ */
    // "×" (or Escape): closed, the conversation kept for next time; focus
    // goes back to the button it was opened from.
    close(restoreFocus = true) {
      closeKodeAi({ restoreFocus });
    },
    onEscape(event) {
      if (event.defaultPrevented) return;
      if (this.listening) {
        this.stopDictation();
        return;
      }
      this.close();
    },
    // The cursor goes in the box — not on a phone, where the keyboard would cover the answers.
    focusInput(evenOnTouch = false) {
      if (!this.open) return;
      if (this.isTouch && !evenOnTouch) {
        this.$refs.panel?.focus({ preventScroll: true });
        return;
      }
      const input = this.$refs.input;
      if (!input) {
        this.$refs.panel?.focus({ preventScroll: true });
        return;
      }
      input.focus();
      const end = input.value.length;
      try {
        input.setSelectionRange(end, end);
      } catch (error) {
        // Not a text field any more.
      }
    },
    // To the newest message — or, in a new conversation, to the top of its hello.
    scrollToBottom(smooth = true) {
      const scroll = this.$refs.scroll;
      if (!scroll) return;
      const top = this.messages.length ? scroll.scrollHeight : 0;
      try {
        scroll.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
      } catch (error) {
        scroll.scrollTop = top;
      }
      if (!smooth) this.onScroll();
    },
    onScroll() {
      const scroll = this.$refs.scroll;
      this.scrolled = Boolean(scroll && scroll.scrollTop > 0);
    },
    // The support chat opening over this panel: this one gets out of its way.
    onDocumentClick(event) {
      if (!this.open) return;
      const target = event.target;
      if (target && typeof target.closest === 'function' && target.closest('.scw-launcher')) this.close(false);
    },
    // A link in an answer to a page of the app goes through the router; on a
    // phone the panel covers the page, so it tucks itself away.
    onContentClick(event) {
      const target = event.target;
      const link = target && typeof target.closest === 'function' ? target.closest('a[data-kai-link="internal"]') : null;
      if (!link) return;
      event.preventDefault();
      const href = link.getAttribute('href');
      if (!href) return;
      const resolved = this.$router.resolve(href);
      if (!resolved || resolved.name === NOT_FOUND_404_ROUTE) return;
      if (this.$route.fullPath !== href) this.$router.push(href).catch(() => {});
      if (window.innerWidth < PHONE_MAX_WIDTH) this.close(false);
    },

    /* ------------------------------- asking ------------------------------ */
    onKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && !this.isTouch) {
        event.preventDefault();
        this.submit();
      }
    },
    // The box grows with what is typed, up to INPUT_MAX_HEIGHT_PX, then scrolls.
    autogrow() {
      const input = this.$refs.input;
      if (!input) return;
      input.style.height = '';
      if (!input.value) {
        input.style.overflowY = 'hidden';
        return;
      }
      const borders = input.offsetHeight - input.clientHeight;
      const wanted = input.scrollHeight + borders;
      input.style.height = `${Math.min(wanted, INPUT_MAX_HEIGHT_PX)}px`;
      input.style.overflowY = wanted > INPUT_MAX_HEIGHT_PX ? 'auto' : 'hidden';
    },
    submit() {
      if (this.listening) this.stopDictation();
      if (!this.canSend) return;
      this.ask(this.draft);
    },
    /** Asks a question: the text becomes the user's message and goes to the assistant. */
    ask(text, { byVoice = false } = {}) {
      const question = String(text || '').trim();
      if (!question || this.thinking) return;
      this.draft = '';
      this.$nextTick(() => this.autogrow());
      const message = {
        id: kodeAiMessageId(),
        role: 'user',
        text: question.length > this.maxLength ? question.slice(0, this.maxLength) : question,
        status: 'sending',
        byVoice,
        createdAt: new Date().toISOString(),
      };
      this.messages.push(message);
      this.send(message);
    },
    async send(message, audio = null) {
      this.stopReading();
      this.thinking = true;
      this.thinkingSlow = false;
      window.clearTimeout(this.slowTimer);
      this.slowTimer = window.setTimeout(() => {
        this.thinkingSlow = true;
      }, SLOW_AFTER_MS);
      this.$nextTick(() => this.scrollToBottom(true));

      const history = kodeAiHistoryOf(this.messages.filter((item) => item.id !== message.id));
      const result = await askKodeAi({
        message: audio ? null : message.text,
        audio,
        history,
        page: kodeAiPageOf(this.$route),
      });
      if (this.isGone) return;

      window.clearTimeout(this.slowTimer);
      this.thinking = false;
      this.thinkingSlow = false;

      if (result.ok && result.data && result.data.answer) {
        if (audio) {
          delete this.pendingAudio[message.id];
          if (result.data.transcript) message.text = result.data.transcript;
        }
        message.status = 'done';
        message.error = null;
        const answer = {
          id: kodeAiMessageId(),
          role: 'assistant',
          text: result.data.answer,
          model: result.data.model,
          createdAt: new Date().toISOString(),
        };
        this.messages.push(answer);
        this.$nextTick(() => {
          this.scrollToBottom(true);
          if (!this.isTouch) this.focusInput();
        });
        // A spoken question is answered aloud; with voice replies on, every one is.
        if (this.voiceReplies || message.byVoice) this.readAloud(answer);
        return;
      }

      message.status = 'failed';
      message.error = result.error
        || (result.status ? 'Kode couldn\'t answer just now. Please try again.' : 'Kode couldn\'t be reached. Check your connection and try again.');
      this.$nextTick(() => this.scrollToBottom(true));
    },
    retry(message) {
      if (this.thinking) return;
      message.status = 'sending';
      message.error = null;
      this.send(message, this.pendingAudio[message.id] || null);
    },
    discard(message) {
      delete this.pendingAudio[message.id];
      this.messages = this.messages.filter((item) => item.id !== message.id);
    },
    newConversation() {
      if (this.thinking) return;
      this.stopVoice();
      this.stopReading();
      this.pendingAudio = {};
      this.messages = [];
      this.draft = '';
      this.$nextTick(() => {
        this.autogrow();
        this.scrollToBottom(false);
        this.focusInput();
      });
    },

    /* ------------------------------- voice in ---------------------------- */
    toggleVoice() {
      if (this.listening) {
        this.stopDictation();
        return;
      }
      if (this.voiceMode === 'dictation') this.startDictation();
      else if (this.voiceMode === 'recording') this.startRecording();
    },
    // Dictation: the words land in the box as they are spoken. Once the
    // speaker pauses it stops by itself and the question is sent; tapping the
    // microphone stops it and leaves the words to be edited.
    startDictation() {
      this.stopReading();
      this.dictationBase = this.draft.trim();
      this.dictationFinal = '';
      this.dictationStopRequested = false;
      this.dictation = createKodeDictation({
        onResult: this.onDictationResult,
        onEnd: this.onDictationEnd,
        onError: this.onDictationError,
      });
      if (!this.dictation) {
        this.voiceMode = isRecordingSupported() ? 'recording' : '';
        if (this.voiceMode) this.startRecording();
        return;
      }
      try {
        this.dictation.start();
        this.listening = true;
      } catch (error) {
        this.dictation = null;
        this.onDictationError('audio-capture');
      }
    },
    onDictationResult(finalText, interimText) {
      this.dictationFinal = finalText;
      this.draft = [this.dictationBase, finalText, interimText].filter(Boolean).join(' ');
      this.$nextTick(() => this.autogrow());
    },
    onDictationEnd() {
      const wasListening = this.listening;
      this.listening = false;
      this.dictation = null;
      if (!wasListening) return;
      const text = [this.dictationBase, this.dictationFinal].filter(Boolean).join(' ').trim();
      this.draft = text;
      this.$nextTick(() => this.autogrow());
      if (!this.dictationStopRequested && this.dictationFinal.trim()) {
        this.ask(text, { byVoice: true });
        return;
      }
      if (text) this.focusInput(true);
    },
    onDictationError(code) {
      // The browser ends the dictation after an error: what was heard stays in the box, unsent.
      this.dictationStopRequested = true;
      this.listening = false;
      switch (code) {
        case 'not-allowed':
        case 'service-not-allowed':
          this.$toast.error('Microphone access is blocked. Allow it for this site in your browser, then try again.', TOAST_GLOBAL_CONFIG);
          break;
        case 'no-speech':
          this.$toast.info('Kode didn\'t hear anything. Tap the microphone and ask again.', TOAST_GLOBAL_CONFIG);
          break;
        case 'audio-capture':
          this.$toast.error('No microphone was found.', TOAST_GLOBAL_CONFIG);
          break;
        case 'network':
          // Dictation runs on the browser's speech service; without it, a
          // recording the assistant transcribes does the same job.
          if (isRecordingSupported()) {
            this.voiceMode = 'recording';
            this.$toast.info('Live dictation isn\'t available right now — record your question instead.', TOAST_GLOBAL_CONFIG);
          } else {
            this.$toast.error('Voice input isn\'t available right now. Please type your question.', TOAST_GLOBAL_CONFIG);
          }
          break;
        case 'aborted':
          break;
        default:
          this.$toast.error('Voice input didn\'t work. Please type your question.', TOAST_GLOBAL_CONFIG);
      }
    },
    stopDictation() {
      this.dictationStopRequested = true;
      if (this.dictation) {
        try {
          this.dictation.stop();
        } catch (error) {
          // Already stopped.
        }
      }
      this.listening = false;
    },
    startRecording() {
      this.stopReading();
      this.recording = true;
    },
    // The recorder's send: the recording goes to the assistant, which says
    // what it heard (shown as the question) and answers it.
    async onRecorded(voice) {
      this.recording = false;
      if (!voice || !voice.blob) return;
      let audio;
      try {
        audio = await recordingToBase64(voice.blob);
      } catch (error) {
        this.$toast.error('The recording couldn\'t be read. Please try again.', TOAST_GLOBAL_CONFIG);
        return;
      }
      if (this.isGone) return;
      const message = {
        id: kodeAiMessageId(),
        role: 'user',
        text: `Voice question (${formatSeconds(voice.duration || 0)})`,
        status: 'sending',
        byVoice: true,
        createdAt: new Date().toISOString(),
      };
      this.pendingAudio[message.id] = audio;
      this.messages.push(message);
      this.send(message, audio);
    },
    stopVoice() {
      if (this.listening) this.stopDictation();
      this.recording = false;
    },

    /* ------------------------------ voice out ---------------------------- */
    readAloud(message) {
      if (!this.speechSupported || !message) return;
      this.speakingId = message.id;
      speakKodeAnswer(message.text, () => {
        if (this.speakingId === message.id) this.speakingId = null;
      });
    },
    stopReading() {
      if (!this.speechSupported) return;
      stopSpeaking();
      this.speakingId = null;
    },
    toggleSpeak(message) {
      if (this.speakingId === message.id) this.stopReading();
      else this.readAloud(message);
    },
    toggleVoiceReplies() {
      this.voiceReplies = !this.voiceReplies;
      writeStorage('localStorage', KODE_AI_VOICE_REPLIES_KEY, this.voiceReplies ? '1' : null);
      if (!this.voiceReplies) this.stopReading();
    },

    /* -------------------------------- copy ------------------------------- */
    async copyAnswer(message) {
      const text = kodeAiPlainText(message.text);
      let copied = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied) {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }
        document.body.removeChild(area);
      }
      if (!copied) {
        this.$toast.error('The answer couldn\'t be copied.', TOAST_GLOBAL_CONFIG);
        return;
      }
      this.copiedId = message.id;
      window.clearTimeout(this.copiedTimer);
      this.copiedTimer = window.setTimeout(() => {
        if (this.copiedId === message.id) this.copiedId = null;
      }, 1600);
    },
  },
  created() {
    // Plain fields: nothing in the template reads them.
    this.isGone = false;
    this.dictation = null;
    this.dictationBase = '';
    this.dictationFinal = '';
    this.dictationStopRequested = false;
    // A recorded question still waiting for its answer, by message id, so "Try again" can send it once more.
    this.pendingAudio = {};
    this.slowTimer = null;
    this.copiedTimer = null;
  },
  mounted() {
    // The top bars' buttons show from now on.
    kodeAiPanel.ready = true;
    document.addEventListener('click', this.onDocumentClick, true);
    if (this.open) this.$nextTick(() => this.scrollToBottom(false));
  },
  beforeUnmount() {
    this.isGone = true;
    // Gone (signed out, or off the signed-in pages): the buttons go too, and it opens closed next time.
    kodeAiPanel.ready = false;
    closeKodeAi({ restoreFocus: false });
    writeStorage('sessionStorage', KODE_AI_OPEN_KEY, null);
    document.removeEventListener('click', this.onDocumentClick, true);
    window.clearTimeout(this.slowTimer);
    window.clearTimeout(this.copiedTimer);
    this.stopVoice();
    this.stopReading();
  },
};
</script>
<style scoped>
/* =========================================================================
   KodeAI's panel, in the support chat's look (the popup at the bottom
   right: components/chat/Support_Chat_Widget, and its conversation in
   assets/style/chat.css): flat — Inter, a pale page, white cards and
   answers with a hairline border, the user's questions in the chat's solid
   indigo, and no gradients but the icon's own. A new conversation is drawn
   as the chat's Home (the hello on a pale tint, cards over its foot); a
   conversation has its white header, its message runs and its plain
   composer. It hangs from the top bar on the right, below the button that
   opens it, and stops short of the support chat's button at the bottom
   right. Sizes are the design's at the apps' 90% zoom (html.app-zoomed).
   Just above the support chat (980), under dialogs (1000 and up).
   ========================================================================= */
.kai {
  --kai-gap: 24px;
  /* Under the top bar (components/Navbar, 4rem tall). */
  --kai-top: calc(4rem + 10px);
  /* The screen's height as the app counts it: 100vh, or more when zoomed (below). */
  --kai-vh: 100vh;
  /* Kept free below the panel: the support chat's button and its margins. */
  --kai-bottom-room: 104px;
  /* The support chat's palette, value for value (chat.css and the popup's own). */
  --kai-accent: #4B3FE5;
  --kai-accent-hover: #3E32D2;
  --kai-accent-soft: #EEEDFD;
  --kai-accent-border: #CBC7F6;
  --kai-text: #18181B;
  --kai-text-2: #52525B;
  --kai-text-3: #71717A;
  --kai-text-4: #A1A1AA;
  /* The user's own name-and-time line. */
  --kai-text-mine: #8A93A3;
  /* The composer's placeholder, and its microphone. */
  --kai-placeholder: #3F3F46;
  --kai-icon: #141B34;
  --kai-border: #E4E4E7;
  --kai-border-strong: #D4D4D8;
  --kai-hairline: #EAEAEC;
  --kai-divider: #F0F0F1;
  --kai-composer-line: #EBEBEB;
  --kai-muted: #F4F4F5;
  --kai-page: #FAFAFA;
  --kai-hero: #EEF0FF;
  --kai-surface: #FFFFFF;
  --kai-danger: #EF4444;
  --kai-font: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  /* The support chat's recorder (components/chat/Chat_Voice_Recorder) draws
     with the chat's own names, which it inherits from here. */
  --sc-primary: var(--kai-accent);
  --sc-primary-hover: var(--kai-accent-hover);
  --sc-primary-soft: var(--kai-accent-soft);
  --sc-primary-border: var(--kai-accent-border);
  --sc-canvas: var(--kai-surface);
  --sc-soft: var(--kai-page);
  --sc-muted-bg: var(--kai-muted);
  --sc-text: var(--kai-text);
  --sc-text-2: var(--kai-text-2);
  --sc-text-3: var(--kai-text-3);
  --sc-text-4: var(--kai-text-4);
  --sc-text-mine: var(--kai-text-mine);
  --sc-placeholder: var(--kai-placeholder);
  --sc-icon: var(--kai-icon);
  --sc-border: var(--kai-border);
  --sc-hairline: var(--kai-hairline);
  --sc-divider: var(--kai-composer-line);
  --sc-border-strong: var(--kai-border-strong);
  --sc-danger: var(--kai-danger);
  --sc-theirs: var(--kai-surface);
  --sc-shadow-lg: 0 10px 28px rgba(24, 24, 27, 0.12);
  --sc-focus: 0 0 0 3px rgba(75, 63, 229, 0.16);
  --sc-font: var(--kai-font);
  position: fixed;
  top: var(--kai-top);
  right: var(--kai-gap);
  z-index: 981;
  color: var(--kai-text);
  font-family: var(--kai-font);
  pointer-events: none;
  -webkit-font-smoothing: antialiased;
}

/* The app renders at 90% on wide screens (html.app-zoomed): the panel's
   share of the screen is counted before that. */
:global(html.app-zoomed) .kai {
  --kai-vh: calc(100vh / 0.9);
}

.kai > * {
  pointer-events: auto;
}

.kai button,
.kai textarea {
  font-family: inherit;
}

/* Focus rings, in the chat's indigo. The apps take the box-shadow, outline
   and border colour off every focused or pressed button (assets/style/
   index.css, !important), so each ring is drawn on the button's ::after,
   which that rule leaves alone. */
.kai-icon-btn::after,
.kai-ask__row::after,
.kai-tool::after,
.kai-mic::after,
.kai-send::after,
.kai-msg__status-btn::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  box-shadow: 0 0 0 2px var(--kai-accent);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease;
}

.kai-icon-btn:focus-visible::after,
.kai-ask__row:focus-visible::after,
.kai-tool:focus-visible::after,
.kai-mic:focus-visible::after,
.kai-send:focus-visible::after,
.kai-msg__status-btn:focus-visible::after {
  opacity: 1;
}

/* ------------------------------- the panel ------------------------------- */
.kai-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 384px;
  height: min(680px, calc(var(--kai-vh) - var(--kai-top) - var(--kai-bottom-room)));
  max-width: calc(100vw - 2 * var(--kai-gap));
  overflow: hidden;
  border: 1px solid var(--kai-border);
  border-radius: 14.5px;
  background: var(--kai-page);
  box-shadow: 0 16px 40px rgba(24, 24, 27, 0.14), 0 2px 8px rgba(24, 24, 27, 0.06);
  outline: none;
  transform-origin: top right;
}

.kai-panel-enter-active,
.kai-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.24s cubic-bezier(0.34, 1.2, 0.64, 1);
}

/* It drops down from the top bar. */
.kai-panel-enter-from,
.kai-panel-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.97);
}

/* A conversation's header, white as the support chat's: the mark,
   "KodeAI", and the buttons. */
.kai-head {
  position: relative;
  z-index: 1;
  display: flex;
  flex: none;
  align-items: center;
  gap: 2px;
  height: 60px;
  padding: 0 16px 0 18px;
  border-bottom: 1px solid var(--kai-border);
  background: var(--kai-surface);
  box-shadow: 0 1px 2px rgba(24, 24, 27, 0.03);
}

/* The mark in a pale circle, as the support chat shows a mark. */
.kai-head__mark {
  box-sizing: border-box;
  display: inline-flex;
  flex: none;
  width: 30px;
  height: 30px;
  margin-right: 8px;
  padding: 5px;
  border-radius: 50%;
  background: var(--kai-accent-soft);
}

.kai-head__title {
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--kai-text);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kai-icon-btn {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--kai-text);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.kai-icon-btn svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.kai-icon-btn:hover:not(:disabled) {
  background: var(--kai-muted);
}

.kai-icon-btn:disabled {
  color: var(--kai-text-4);
  cursor: default;
}

/* Voice replies on. */
.kai-icon-btn.is-on,
.kai-icon-btn.is-on:hover:not(:disabled) {
  background: var(--kai-accent-soft);
  color: var(--kai-accent);
}

/* On the hello's tint. */
.kai-icon-btn--hero:hover:not(:disabled) {
  background: rgba(75, 63, 229, 0.08);
}

.kai-icon-btn--hero.is-on,
.kai-icon-btn--hero.is-on:hover:not(:disabled) {
  background: rgba(75, 63, 229, 0.12);
}

/* What sits between the header and the composer: the hello, or the conversation. */
.kai-scroll {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(24, 24, 27, 0.18) transparent;
}

.kai-scroll::-webkit-scrollbar {
  width: 6px;
}

.kai-scroll::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(24, 24, 27, 0.18);
}

@keyframes kai-view-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

/* ------------------------- a new conversation ---------------------------- */
/* Its header, on the support chat's pale tint: the mark, the name and the
   buttons. It stays put while the hello and the cards scroll under it, and
   a hairline marks its foot once something has. At rest it reads as the
   top of the hello, as the support chat's Home bar does. */
.kai-home-head {
  position: relative;
  z-index: 1;
  display: flex;
  flex: none;
  align-items: center;
  gap: 2px;
  padding: 16px 16px 15px 22px;
  border-bottom: 1px solid transparent;
  background: var(--kai-hero);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.kai-home-head.is-scrolled {
  border-bottom-color: var(--kai-border);
  box-shadow: 0 1px 2px rgba(24, 24, 27, 0.03);
}

/* The mark on a white tile, as the support chat's Home shows its team's. */
.kai-home-head__logo {
  box-sizing: border-box;
  display: inline-flex;
  flex: none;
  width: 38px;
  height: 38px;
  padding: 6px;
  border-radius: 10px;
  background: var(--kai-surface);
  box-shadow: 0 1px 3px rgba(24, 24, 27, 0.08);
}

.kai-home-head__name {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
  overflow: hidden;
  color: var(--kai-text);
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kai-home {
  display: flex;
  flex: none;
  flex-direction: column;
  animation: kai-view-in 0.2s ease-out;
}

/* The hello, on the header's tint (28px under its bar, as before the bar
   stayed put), with the cards overlapping its foot. */
.kai-hero {
  flex: none;
  padding: 12px 16px 66px 22px;
  background: var(--kai-hero);
  color: var(--kai-text);
}

.kai-hero__title {
  margin: 0;
  color: var(--kai-text);
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.kai-hero__wave {
  display: inline-block;
  animation: kai-wave 2.4s ease-in-out 0.4s 2;
  transform-origin: 70% 80%;
}

@keyframes kai-wave {
  0%, 60%, 100% { transform: rotate(0); }
  10%, 30% { transform: rotate(14deg); }
  20%, 40% { transform: rotate(-8deg); }
}

.kai-hero__text {
  margin: 4px 0 0;
  color: var(--kai-text-3);
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.015em;
  line-height: 1.25;
}

.kai-cards {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: -44px;
  padding: 0 16px 18px;
}

.kai-card {
  display: block;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--kai-border);
  border-radius: 12px;
  background: var(--kai-surface);
  color: var(--kai-text);
  text-align: left;
  box-shadow: 0 4px 14px rgba(24, 24, 27, 0.06);
}

.kai-card__label {
  display: block;
  color: var(--kai-text-3);
  font-size: 12px;
  font-weight: 500;
}

/* Questions to start with: a list on the card, as the chat lists its
   conversations — a row each, a hairline between. */
.kai-ask {
  overflow: hidden;
  padding: 0;
}

.kai-ask .kai-card__label {
  padding: 14px 16px 4px;
}

.kai-ask__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 16px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--kai-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

/* The hairline on ::before: a focused button's own border is taken away (see the rings). */
.kai-ask__row + .kai-ask__row::before {
  content: '';
  position: absolute;
  top: 0;
  right: 16px;
  left: 16px;
  height: 1px;
  background: var(--kai-divider);
  pointer-events: none;
}

.kai-ask__row:last-child {
  padding-bottom: 12px;
}

/* Its ring inside the card, which clips the rows' corners. */
.kai-ask__row::after {
  inset: 0;
  border-radius: 0;
  box-shadow: inset 0 0 0 2px var(--kai-accent);
}

.kai-ask__row:hover:not(:disabled) {
  background: var(--kai-page);
}

.kai-ask__row:disabled {
  opacity: 0.6;
  cursor: default;
}

.kai-ask__text {
  flex: 1;
  min-width: 0;
}

.kai-ask__row .pi {
  flex: none;
  color: var(--kai-text-4);
  font-size: 14px;
  transition: color 0.12s ease, transform 0.15s ease;
}

.kai-ask__row:hover:not(:disabled) .pi {
  color: var(--kai-accent);
  transform: translateX(2px);
}

/* ---------------------------- the conversation --------------------------- */
.kai-thread {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  padding: 16px 18px;
  animation: kai-view-in 0.2s ease-out;
}

/* Few messages sit at the bottom, by the composer, as in any messenger. */
.kai-thread > :first-child {
  margin-top: auto;
}

/* A run of messages from one side sits under one line: Kode's mark, who
   wrote it and when ("You" for the user's). Kode's run is on the left,
   lined up under the name past the mark; the user's is on the right. */
.kai-msg {
  display: flex;
  align-items: flex-start;
  max-width: 100%;
  margin-top: 6px;
  animation: kai-msg-in 0.18s ease-out;
}

.kai-msg--first {
  margin-top: 20px;
}

.kai-msg--user {
  flex-direction: row-reverse;
}

@keyframes kai-msg-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

.kai-msg__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* Answers are long: they may take the whole width past the mark. */
.kai-msg--assistant .kai-msg__main {
  align-items: flex-start;
  max-width: 100%;
  padding-left: 27px;
}

.kai-msg--user .kai-msg__main {
  align-items: flex-end;
  max-width: 84%;
}

.kai-msg__head {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
  height: 22px;
  margin-bottom: 1px;
  color: var(--kai-text-2);
  font-size: 10px;
  line-height: 22px;
  white-space: nowrap;
}

.kai-msg--assistant .kai-msg__head {
  margin-left: -27px;
}

.kai-msg--user .kai-msg__head {
  justify-content: flex-end;
  color: var(--kai-text-mine);
}

.kai-msg__name {
  overflow: hidden;
  text-overflow: ellipsis;
}

.kai-msg__time {
  flex: none;
  margin-left: -1.5px;
}

/* Kode's mark: a white disc with a hairline ring, as the chat shows the other side. */
.kai-avatar {
  box-sizing: border-box;
  display: inline-flex;
  flex: none;
  width: 22px;
  height: 22px;
  padding: 3.5px;
  border-radius: 50%;
  background: var(--kai-surface);
  box-shadow: inset 0 0 0 1px var(--kai-hairline);
}

/* Bubbles: flat, gently rounded, the same at every corner — white with a
   hairline border for Kode's answers, solid indigo for the user's questions. */
.kai-bubble {
  position: relative;
  max-width: 100%;
  margin: 0;
  padding: 10px 15px;
  border: 1px solid var(--kai-hairline);
  border-radius: 8px;
  background: var(--kai-surface);
  color: var(--kai-text);
  font-size: 14px;
  line-height: 24px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.kai-bubble--mine {
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  border-color: var(--kai-accent);
  background: var(--kai-accent);
  color: #FFFFFF;
  white-space: pre-wrap;
}

.kai-bubble__voice {
  flex: none;
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.85;
}

/* A question still with Kode, or one that did not go. */
.kai-msg.is-sending .kai-bubble--mine {
  opacity: 0.78;
}

.kai-msg.is-failed .kai-bubble--mine {
  opacity: 0.6;
}

/* The answer's markdown (utils/kode_ai renderKodeMarkdown). */
.kai-md :deep(p) {
  margin: 0;
}

.kai-md :deep(p + p),
.kai-md :deep(p + ol),
.kai-md :deep(p + ul),
.kai-md :deep(ol + p),
.kai-md :deep(ul + p),
.kai-md :deep(ol + ul),
.kai-md :deep(ul + ol),
.kai-md :deep(ol + ol),
.kai-md :deep(ul + ul) {
  margin-top: 8px;
}

.kai-md :deep(.kai-md__heading) {
  font-weight: 600;
}

.kai-md :deep(ol),
.kai-md :deep(ul) {
  margin: 0;
  padding-left: 22px;
}

.kai-md :deep(li) {
  margin: 2px 0;
  padding-left: 2px;
}

.kai-md :deep(li::marker) {
  color: var(--kai-accent);
  font-weight: 600;
}

.kai-md :deep(strong) {
  color: var(--kai-text);
  font-weight: 600;
}

.kai-md :deep(code) {
  padding: 1px 5px;
  border-radius: 5px;
  background: var(--kai-accent-soft);
  color: var(--kai-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
}

/* Links, as the chat's. */
.kai-md :deep(a) {
  color: var(--kai-accent-hover);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.kai-md :deep(a:hover) {
  color: var(--kai-accent);
}

/* Read aloud and copy, under an answer: round, as the chat's message buttons. */
.kai-msg__tools {
  display: flex;
  gap: 2px;
  margin: 3px 0 0 -5px;
}

.kai-tool {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--kai-text-3);
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.kai-tool:hover {
  background: var(--kai-muted);
  color: var(--kai-text);
}

.kai-tool.is-on,
.kai-tool.is-on:hover {
  color: var(--kai-accent);
}

/* "Not sent. Try again · Remove" */
.kai-msg__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 5px;
  padding: 0 1px;
  color: var(--kai-danger);
  font-size: 11px;
}

.kai-msg__status-btn {
  position: relative;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.kai-msg__status-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* "KodeAI is thinking…", as the chat shows someone typing. */
.kai-typing {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 2px 27px;
  color: var(--kai-text-3);
  font-size: 11.5px;
}

.kai-typing__dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 9px 11px;
  border: 1px solid var(--kai-hairline);
  border-radius: 8px;
  background: var(--kai-surface);
}

.kai-typing__dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--kai-text-4);
  animation: kai-typing 1.2s ease-in-out infinite;
}

.kai-typing__dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.kai-typing__dots i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes kai-typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-4px); opacity: 1; }
}

/* ------------------------------ the composer ----------------------------- */
/* The support chat's: a plain line, the words on the left and the
   microphone — or the send arrow — on the right, level with its last line. */
.kai-foot {
  flex: none;
  padding: 0 16px env(safe-area-inset-bottom, 0px) 20px;
  border-top: 1px solid var(--kai-composer-line);
  background: var(--kai-surface);
}

.kai-listening {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0 0;
  padding: 7px 10px;
  border-radius: 8px;
  background: #FEF2F2;
  color: #B91C1C;
  font-size: 12px;
  line-height: 1.4;
}

.kai-listening__dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--kai-danger);
  animation: kai-pulse 1.1s ease-in-out infinite;
}

@keyframes kai-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.6); opacity: 0.5; }
}

.kai-composer {
  display: flex;
  align-items: flex-end;
}

/* The apps give every textarea !important borders, sizes and placeholder
   colour: the line restates its own, as the chat's does. */
.kai-composer .kai-composer__input,
.kai-composer .kai-composer__input:focus {
  flex: 1 1 auto;
  min-width: 0;
  height: auto;
  min-height: 56px;
  max-height: 160px;
  margin: 0;
  padding: 16px 8px 16px 0;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent;
  color: var(--kai-text);
  font-family: inherit;
  font-size: 14px !important;
  line-height: 24px;
  resize: none;
  overflow-y: hidden;
  outline: none !important;
  box-shadow: none !important;
  scrollbar-width: thin;
  scrollbar-color: rgba(31, 35, 48, 0.2) transparent;
}

.kai-composer .kai-composer__input::placeholder {
  overflow: hidden;
  color: var(--kai-placeholder) !important;
  font-size: 14px;
  line-height: 24px;
  opacity: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kai-mic,
.kai-send {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-bottom: 11px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
}

.kai-mic svg,
.kai-send svg {
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* The microphone: a tool, as the chat's. */
.kai-mic {
  background: transparent;
  color: var(--kai-icon);
}

.kai-mic svg {
  width: 24px;
  height: 24px;
  stroke-width: 1.5;
}

.kai-mic:hover:not(:disabled) {
  background: var(--kai-muted);
}

/* Listening: red, and it glows — on ::before, as a pressed button keeps no shadow of its own. */
.kai-mic.is-on,
.kai-mic.is-on:hover:not(:disabled) {
  background: var(--kai-danger);
  color: #FFFFFF;
}

.kai-mic.is-on::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  animation: kai-mic-glow 1.4s ease-in-out infinite;
}

@keyframes kai-mic-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45); }
  50% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
}

/* The send arrow: a small solid square in the microphone's place. */
.kai-send {
  background: var(--kai-accent);
  color: #FFFFFF;
}

.kai-send svg {
  width: 20px;
  height: 20px;
  stroke-width: 1.8;
}

.kai-send:hover:not(:disabled) {
  background: var(--kai-accent-hover);
}

.kai-mic:active:not(:disabled),
.kai-send:active:not(:disabled) {
  transform: scale(0.94);
}

.kai-mic:disabled,
.kai-send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* --------------------------- phones and tablets -------------------------- */
/* The panel stops above the bottom navigation and the support chat's button
   raised over it. */
@media (max-width: 991.98px) {
  .kai {
    --kai-gap: 16px;
    --kai-bottom-room: calc(152px + env(safe-area-inset-bottom, 0px));
  }
}

/* Phones: the open panel fills the screen, and its own header closes it. */
@media (max-width: 575.98px) {
  .kai--open {
    inset: 0;
  }

  .kai-panel {
    width: 100%;
    max-width: none;
    height: 100%;
    border: 0;
    border-radius: 0;
  }

  .kai-head {
    height: calc(54px + env(safe-area-inset-top, 0px));
    padding-top: env(safe-area-inset-top, 0px);
  }

  .kai-home-head {
    padding-top: calc(16px + env(safe-area-inset-top, 0px));
  }

  .kai-thread {
    padding: 14px;
  }

  .kai-msg--user .kai-msg__main {
    max-width: 90%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kai-home-head,
  .kai-ask__row,
  .kai-ask__row .pi,
  .kai-mic,
  .kai-send,
  .kai-panel-enter-active,
  .kai-panel-leave-active {
    transition: none;
  }

  .kai-home,
  .kai-thread,
  .kai-hero__wave,
  .kai-msg,
  .kai-typing__dots i,
  .kai-listening__dot,
  .kai-mic.is-on::before {
    animation: none;
  }
}
</style>
